import * as XLSX from "xlsx";
import type { Term } from "./types";

export type WordMasterImportRow = {
  word: string;
  meaning_english: string;
  meaning_hindi: string;
  meaning_gujarati: string;
  pronunciation: string;
  example_usage: string;
  contributor_name: string;
  contributor_email: string;
  status: Term["status"];
  is_featured: boolean;
  is_enabled: boolean;
  admin_notes: string;
};

export type WordMasterFieldKey = keyof WordMasterImportRow;

export type WordMasterColumnMapping = Record<
  WordMasterFieldKey,
  string | null
>;

export const WORD_MASTER_FIELD_ORDER: WordMasterFieldKey[] = [
  "word",
  "meaning_english",
  "meaning_hindi",
  "meaning_gujarati",
  "pronunciation",
  "example_usage",
  "contributor_name",
  "contributor_email",
  "status",
  "is_featured",
  "is_enabled",
  "admin_notes",
];

export const WORD_MASTER_FIELD_LABELS: Record<
  WordMasterFieldKey,
  { label: string; required: boolean }
> = {
  word: { label: "Word", required: true },
  meaning_english: { label: "Meaning (English)", required: false },
  meaning_hindi: { label: "Meaning (Hindi)", required: false },
  meaning_gujarati: { label: "Meaning (Gujarati)", required: false },
  pronunciation: { label: "Pronunciation", required: false },
  example_usage: { label: "Example usage", required: false },
  contributor_name: { label: "Contributor name", required: false },
  contributor_email: { label: "Contributor email", required: false },
  status: { label: "Status (approved / pending / draft or rejected)", required: false },
  is_featured: {
    label: "Featured (true/false)",
    required: false,
  },
  is_enabled: {
    label: "Visible on site (true/false)",
    required: false,
  },
  admin_notes: { label: "Admin notes", required: false },
};

/** Normalized header for fuzzy matching only */
function normHeader(key: string): string {
  return key
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function cellStr(v: unknown): string {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

function parseBool(v: unknown, fallback: boolean): boolean {
  if (v === undefined || v === null || v === "") return fallback;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;
  return fallback;
}

function parseStatus(v: unknown): Term["status"] {
  const s = cellStr(v).toLowerCase();
  if (s === "pending") return "pending";
  if (s === "rejected" || s === "draft") return "rejected";
  return "approved";
}

function pickCell(
  row: Record<string, unknown>,
  excelColumn: string | null | undefined
): unknown {
  if (excelColumn == null || excelColumn === "") return undefined;
  if (Object.prototype.hasOwnProperty.call(row, excelColumn)) {
    return row[excelColumn];
  }
  const nk = normHeader(excelColumn);
  for (const k of Object.keys(row)) {
    if (normHeader(k) === nk) return row[k];
  }
  return undefined;
}

const FIELD_MATCH_ALIASES: Record<WordMasterFieldKey, string[]> = {
  word: ["word", "term", "shabd", "title"],
  meaning_english: [
    "meaning_english",
    "english",
    "meaning_en",
    "meaningenglish",
    "definition",
  ],
  meaning_hindi: ["meaning_hindi", "hindi", "meaning_hi"],
  meaning_gujarati: ["meaning_gujarati", "gujarati", "guj", "meaning_gu"],
  pronunciation: ["pronunciation", "phonetic", "ipa"],
  example_usage: ["example_usage", "example", "usage"],
  contributor_name: ["contributor_name", "contributor", "author_name"],
  contributor_email: [
    "contributor_email",
    "email",
    "contributor_e_mail",
    "author_email",
  ],
  status: ["status", "state"],
  is_featured: ["is_featured", "featured", "feature"],
  is_enabled: ["is_enabled", "is_enable", "enabled", "visible", "published"],
  admin_notes: ["admin_notes", "notes", "internal_notes"],
};

export function emptyWordMasterMapping(): WordMasterColumnMapping {
  return {
    word: null,
    meaning_english: null,
    meaning_hindi: null,
    meaning_gujarati: null,
    pronunciation: null,
    example_usage: null,
    contributor_name: null,
    contributor_email: null,
    status: null,
    is_featured: null,
    is_enabled: null,
    admin_notes: null,
  };
}

/** Guess Excel column → field using normalized header aliases. One Excel column maps to at most one field. */
export function guessWordMasterMapping(
  excelColumns: string[]
): WordMasterColumnMapping {
  const m = emptyWordMasterMapping();
  const usedCols = new Set<string>();

  for (const field of WORD_MASTER_FIELD_ORDER) {
    const aliases = FIELD_MATCH_ALIASES[field].map(normHeader);
    for (const col of excelColumns) {
      if (usedCols.has(col)) continue;
      const nc = normHeader(col);
      if (aliases.includes(nc)) {
        m[field] = col;
        usedCols.add(col);
        break;
      }
    }
  }

  return m;
}

/** First worksheet → raw rows keyed by Excel headers (as returned by SheetJS). */
export function inspectWordMasterWorkbook(buffer: ArrayBuffer): {
  columns: string[];
  rows: Record<string, unknown>[];
} {
  const wb = XLSX.read(buffer, { type: "array" });
  const firstName = wb.SheetNames[0];
  if (!firstName) return { columns: [], rows: [] };
  const sheet = wb.Sheets[firstName];
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
    raw: false,
  });
  if (raw.length === 0) return { columns: [], rows: [] };

  const keysRaw = Object.keys(raw[0]);
  const blankReplacements = new Map<string, string>();
  keysRaw.forEach((k, i) => {
    const trimmed = k.replace(/^\uFEFF/, "").trim();
    if (trimmed === "") {
      blankReplacements.set(k, `Column_${i + 1}`);
    }
  });

  const columns = keysRaw.map(k => {
    const replaced = blankReplacements.get(k);
    if (replaced) return replaced;
    return k.replace(/^\uFEFF/, "");
  });

  const rows = raw.map(record => {
    const next: Record<string, unknown> = {};
    for (const k of keysRaw) {
      const outKey = blankReplacements.get(k) ?? k.replace(/^\uFEFF/, "");
      next[outKey] = record[k];
    }
    return next;
  });

  return { columns, rows };
}

/** Build import rows using explicit Excel column → system field mapping. */
export function applyWordMasterMapping(
  rows: Record<string, unknown>[],
  mapping: WordMasterColumnMapping
): WordMasterImportRow[] {
  const wordCol = mapping.word;
  if (!wordCol) return [];

  const out: WordMasterImportRow[] = [];
  for (const row of rows) {
    const word = cellStr(pickCell(row, wordCol));
    if (!word) continue;

    out.push({
      word,
      meaning_english: cellStr(pickCell(row, mapping.meaning_english)),
      meaning_hindi: cellStr(pickCell(row, mapping.meaning_hindi)),
      meaning_gujarati: cellStr(pickCell(row, mapping.meaning_gujarati)),
      pronunciation: cellStr(pickCell(row, mapping.pronunciation)),
      example_usage: cellStr(pickCell(row, mapping.example_usage)),
      contributor_name: cellStr(pickCell(row, mapping.contributor_name)),
      contributor_email: cellStr(pickCell(row, mapping.contributor_email)),
      status: parseStatus(pickCell(row, mapping.status)),
      is_featured: parseBool(pickCell(row, mapping.is_featured), false),
      is_enabled: parseBool(pickCell(row, mapping.is_enabled), true),
      admin_notes: cellStr(pickCell(row, mapping.admin_notes)),
    });
  }
  return out;
}

/** @deprecated Use inspectWordMasterWorkbook + mapping UI + applyWordMasterMapping */
export function parseWordMasterWorkbook(buffer: ArrayBuffer): WordMasterImportRow[] {
  const { columns, rows } = inspectWordMasterWorkbook(buffer);
  if (columns.length === 0) return [];
  const mapping = guessWordMasterMapping(columns);
  return applyWordMasterMapping(rows, mapping);
}

/** Export rows as `.xlsx`; pass no terms for a blank template row with headers only. */
export function exportTermsToXlsxBlob(terms: Term[]): Blob {
  const rows =
    terms.length > 0
      ? terms.map(t => ({
          word: t.word,
          meaning_english: t.meaning_english ?? "",
          meaning_hindi: t.meaning_hindi ?? "",
          meaning_gujarati: t.meaning_gujarati ?? "",
          pronunciation: t.pronunciation ?? "",
          example_usage: t.example_usage ?? "",
          contributor_name: t.contributor_name ?? "",
          contributor_email: t.contributor_email ?? "",
          status: t.status === "rejected" ? "draft" : t.status,
          is_featured: t.featured,
          is_enabled: t.is_enabled,
          admin_notes: t.admin_notes ?? "",
        }))
      : [
          {
            word: "",
            meaning_english: "",
            meaning_hindi: "",
            meaning_gujarati: "",
            pronunciation: "",
            example_usage: "",
            contributor_name: "",
            contributor_email: "",
            status: "approved",
            is_featured: false,
            is_enabled: true,
            admin_notes: "",
          },
        ];
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Terms");
  const buf = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
